import ConfirmDialog from '@/components/ConfirmDialog';
import TemplatePreview from '@/components/TemplatePreview';
import { useToast } from '@/hooks/useToast';
import { templatesService } from '@/services/templatesService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import {
  CATEGORY_COLORS,
  COMPONENT_TYPES,
  STATUS_COLORS,
  TEMPLATE_STATUSES,
} from '@/types/templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

/**
 * Template detail view with status history and edit tracking
 * @returns {JSX.Element} TemplateDetail component
 */
const TemplateDetail = () => {
  const { projectId, templateId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  // Fetch template details
  const {
    data: templateData,
    isLoading: templateLoading,
    error: templateError,
  } = useQuery({
    queryKey: ['template', projectId, templateId],
    queryFn: () => templatesService.getTemplate(projectId, templateId),
    enabled: !!projectId && !!templateId,
  });

  // Fetch WhatsApp configuration for preview branding
  const { data: whatsappConfigData } = useQuery({
    queryKey: ['whatsapp-config', projectId],
    queryFn: () => whatsappConfigService.getWhatsAppConfig(projectId),
    enabled: !!projectId,
  });

  const whatsappConfig = whatsappConfigData?.data?.whatsappConfig;

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: () => templatesService.deleteTemplate(projectId, templateId),
    onSuccess: () => {
      showSuccess('Template deleted successfully');
      queryClient.invalidateQueries(['templates', projectId]);
      queryClient.invalidateQueries(['template-stats', projectId]);
      navigate(`/projects/${projectId}/templates`);
    },
    onError: error => {
      showError(error.message || 'Failed to delete template');
    },
  });

  // Resume template mutation
  const resumeTemplateMutation = useMutation({
    mutationFn: () => templatesService.resumeTemplate(projectId, templateId),
    onSuccess: () => {
      showSuccess('Template resumed successfully');
      queryClient.invalidateQueries(['template', projectId, templateId]);
    },
    onError: error => {
      showError(error.message || 'Failed to resume template');
    },
  });

  // Handle delete template
  const handleDeleteTemplate = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteTemplateMutation.mutate();
    setShowDeleteDialog(false);
  };

  // Handle resume template
  const handleResumeTemplate = () => {
    setShowResumeDialog(true);
  };

  const confirmResume = () => {
    resumeTemplateMutation.mutate();
    setShowResumeDialog(false);
  };
  const template = templateData?.data;

  // Transform template and extract example params for preview
  // Logic aligned with BroadcastCreate.jsx for consistency
  const { selectedTemplate, headerParams, bodyParams, buttonParams } =
    useMemo(() => {
      if (!template) {
        return {
          selectedTemplate: null,
          headerParams: {},
          bodyParams: {},
          buttonParams: {},
        };
      }

      const header = template.components?.find(
        c => c.type === COMPONENT_TYPES.HEADER
      );
      const body = template.components?.find(
        c => c.type === COMPONENT_TYPES.BODY
      );
      const buttons =
        template.components
          ?.filter(c => c.type === COMPONENT_TYPES.BUTTONS)
          ?.map(btn => ({
            ...btn,
            type: btn.buttonType, // Align with TemplatePreview's expected 'type' prop
          })) || [];

      // Extract parameters and fill with examples for the detail view
      const hParams = {};
      if (header?.text) {
        const matches = header.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach(match => {
            const variable = match.replace(/[{}]/g, '');
            hParams[variable] = header.example?.header_text?.[0] || match;
          });
        }
      }

      const bParams = {};
      if (body?.text) {
        const matches = body.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach((match, idx) => {
            const variable = match.replace(/[{}]/g, '');
            // Use provided example if available, otherwise fallback to the placeholder
            const exampleValue = body.example?.body_text?.[0]?.[idx];
            bParams[variable] = exampleValue || match;
          });
        }
      }

      const btnParams = {};
      buttons.forEach((btn, idx) => {
        if (btn.type === 'copy_code') {
          btnParams[`copy_code_${idx}`] = btn.example || 'CODE123';
        } else if (btn.type === 'url' && btn.example) {
          btnParams[`url_${idx}`] = btn.example;
        }
      });

      return {
        selectedTemplate: {
          ...template,
        },
        headerParams: hParams,
        bodyParams: bParams,
        buttonParams: btnParams,
      };
    }, [template]);

  if (templateLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading template
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {templateError.message || 'An unexpected error occurred'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">
            Template not found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            The template you're looking for doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <Link
              to={`/projects/${projectId}/templates`}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back to Templates
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/projects/${projectId}/templates`}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {template.name}
                </h1>
                <div className="flex items-center space-x-4 mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${
                      STATUS_COLORS[template.status] ||
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {template.status.charAt(0).toUpperCase() +
                      template.status.slice(1).toLowerCase().replace('_', ' ')}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${
                      CATEGORY_COLORS[template.category] ||
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {template.category.charAt(0).toUpperCase() +
                      template.category.slice(1).toLowerCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/projects/${projectId}/templates/${templateId}/edit`}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Edit Template
            </Link>
            {template.status === TEMPLATE_STATUSES.PAUSED && (
              <button
                onClick={handleResumeTemplate}
                disabled={resumeTemplateMutation.isPending}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
              >
                {resumeTemplateMutation.isPending ? 'Resuming...' : 'Resume'}
              </button>
            )}
            <button
              onClick={handleDeleteTemplate}
              disabled={deleteTemplateMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            >
              {deleteTemplateMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left Side: Tabs and Info */}
        <div className="flex-1 w-full order-2 lg:order-1">
          {/* Tabs */}
          <div className="mb-6">
            <nav className="flex space-x-8">
              {[{ id: 'overview', name: 'Overview' }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Basic Information
                    </h3>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Template Name
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Category
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.category}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Parameter Format
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.parameterFormat}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Language
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.language}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Status
                        </dt>
                        <dd className="mt-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              STATUS_COLORS[template.status] ||
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {template.status}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          WhatsApp Template ID
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.whatsappTemplateId || 'Not available'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Created
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(template.createdAt).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Last Updated
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(template.updatedAt).toLocaleString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Edit Count
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {template.editCount} edits this month
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Rejection Reason */}
                  {template.status === TEMPLATE_STATUSES.REJECTED &&
                    template.rejectionReason && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Rejection Reason
                        </h3>
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                          <p className="text-sm text-red-800">
                            {template.rejectionReason}
                          </p>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Visual Preview Sidebar */}
        <div className="lg:w-96 w-full lg:sticky lg:top-8 order-1 lg:order-2">
          <div className="bg-white shadow rounded-xl p-6 flex flex-col items-center">
            <h3 className="text-lg font-bold text-gray-900 mb-6 w-full flex items-center">
              <span className="w-1.5 h-6 bg-primary-600 rounded-full mr-3"></span>
              Visual Preview
            </h3>
            <TemplatePreview
              template={selectedTemplate}
              headerParams={headerParams}
              bodyParams={bodyParams}
              buttonParams={buttonParams}
              headerFile={null}
              showMobilePreview={true}
              businessName={whatsappConfig?.verifiedName}
              businessLogo={whatsappConfig?.profilePictureUrl}
              className="scale-[0.9] origin-top"
            />
            <div className="mt-8 pt-6 border-t border-gray-100 w-full">
              <div className="flex items-center space-x-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></div>
                <span>WhatsApp Live Preview</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Template"
        message={`Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
        isLoading={deleteTemplateMutation.isPending}
      />

      <ConfirmDialog
        isOpen={showResumeDialog}
        title="Resume Template"
        message={`Are you sure you want to resume the template "${template.name}"? This will move it back to an active status.`}
        confirmText="Resume"
        confirmVariant="primary"
        onConfirm={confirmResume}
        onCancel={() => setShowResumeDialog(false)}
        isLoading={resumeTemplateMutation.isPending}
      />
    </div>
  );
};

export default TemplateDetail;
